import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Loading } from '@pandiafi/design-system'
import { useNavigate } from 'react-router-dom'

import {
  ConfirmOperationType,
  ContractType,
  Id,
} from '../../../../common/types'
import { ResidentAppLocale } from '../../../../common/validations'
import { resolveContractTerminationTerminology } from '../utils/resolveContractTerminationTerminology'

import { useAPIConfirmApproveTermination } from 'api/apiHooks/useAPIConfirmApproveTermination'
import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'
import i18n from 'i18n/i18n'

interface ConfirmContractTerminationFormProps {
  children: React.ReactNode
  contractTerminationRequestId: Id
  contractType: ContractType
  userMetadata: { isImpersonator: boolean }
}

export const ConfirmContractTerminationForm: FC<
  ConfirmContractTerminationFormProps
> = ({
  children,
  contractTerminationRequestId,
  contractType,
  userMetadata,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation(['terminateContract'])
  const terminology = resolveContractTerminationTerminology(contractType)
  const { currentContractId: contractUuid } = useCurrentContract()

  const lang = i18n.language ?? ResidentAppLocale.FI

  const { mutate, isSuccess, data, isPending } =
    useAPIConfirmApproveTermination(contractUuid, lang)

  const handleConfirm = () =>
    mutate({
      operationData: {
        terminationRequestId: contractTerminationRequestId,
      },
      operationType:
        ConfirmOperationType.RESIDENT_APP_ACCEPT_CONTRACT_TERMINATION,
    })

  const handleReject = () =>
    mutate({
      operationData: {
        terminationRequestId: contractTerminationRequestId,
      },
      operationType:
        ConfirmOperationType.RESIDENT_APP_REJECT_CONTRACT_TERMINATION,
    })

  const handleBack = () => {
    navigate(-1)
  }

  if (isSuccess && data && data.link) {
    window.location.href = data.link
  }

  return (
    <>
      {children}
      <div className="flex justify-center">
        <div className="inline-flex flex-col space-y-4">
          {isPending && <Loading />}

          <Button
            variant="delete"
            type="button"
            onClick={handleConfirm}
            disabled={isPending || userMetadata.isImpersonator}
          >
            {t(terminology.confirmButton)}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handleReject}
            disabled={isPending || userMetadata.isImpersonator}
          >
            {t(terminology.rejectButton)}
          </Button>
          <Button variant="text" onClick={handleBack}>
            {t('terminateContract:buttons.back')}
          </Button>
        </div>
      </div>
    </>
  )
}
